import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CreateTokenModal from '../../components/CreateTokenModal';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import LoadingButton from '@/components/ui/LoadingButton';
import IdolSelector from '@/components/ui/IdolSelector';
import Pagination from '@/components/ui/Pagination';
import Axios from '@/shared/api/axios';
import { formatDateTime } from '@/shared/utils/formatDate';

function TokenManagement() {
  const { t } = useTranslation();
  const createTokenModalRef = useRef();
  const statusModalRef = useRef();
  const successModalRef = useRef();
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const [selectedIdol, setSelectedIdol] = useState('');
  const [tokens, setTokens] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteModalRef = useRef(null);
  const [pendingDeleteTokenId, setPendingDeleteTokenId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;
  // 防亂序：快速切換偶像或換頁時，只套用最後一次請求的結果
  const fetchSeqRef = useRef(0);

  const fetchTokens = async (idolId, page = 1) => {
    if (!idolId) return;

    const seq = ++fetchSeqRef.current;
    try {
      const response = await Axios.get(`/dev/tokens`, {
        params: {
          idol_id: idolId,
          page: page,
          limit: limit
        }
      });
      if (seq !== fetchSeqRef.current) return;

      if (response.data?.status_code === 'SYSTEM_1000') {
        setTokens(response.data.tokens || []);
        setTotal(response.data.total || 0);
        setCurrentPage(page);
      } else {
        setCurrentStatusCode(response.data?.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to fetch tokens:', error);
      }
    }
  };

  useEffect(() => {
    if (selectedIdol) {
      // 切換偶像時先清空前一個偶像的列表：撤銷是以目前選擇的偶像送出，
      // 列表若還是前一個偶像的 token，會拿錯的偶像去撤銷；載入失敗時也不該留著舊列表
      setTokens([]);
      setTotal(0);
      setCurrentPage(1);
      fetchTokens(selectedIdol, 1);
    }
  }, [selectedIdol]);

  const hasData = tokens.length > 0;

  const handleCreateToken = () => {
    createTokenModalRef.current?.show();
  };
  const handleClickDelete = (tokenId) => {
    setPendingDeleteTokenId(tokenId);
    deleteModalRef.current?.show();
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await Axios.post('/dev/revoke-token', {
        idol_id: selectedIdol,
        token_id: pendingDeleteTokenId
      });

      const statusCode = response.data?.status_code;
      if (statusCode === 'SYSTEM_1000') {
        // 成功時顯示成功訊息
        await fetchTokens(selectedIdol);
        successModalRef.current?.show();
      } else if (statusCode) {
        // 其他狀態碼顯示錯誤
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error('Failed to delete token:', error);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    } finally {
      setIsDeleting(false);
      deleteModalRef.current?.hide();
    }
  };

  const handleTokenSubmit = async (formData) => {
    try {
      const response = await Axios.post('/dev/create-token', formData);

      const statusCode = response.data?.status_code;

      if (statusCode === 'SYSTEM_1000' && response.data.token) {
        const result = {
          token: response.data.token,
          expiryDate: response.data.expire_at
        };
        createTokenModalRef.current?.showSuccess(result);
      } else if (statusCode) {
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      console.error('Failed to create token:', error);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      }
    }
  };

  const handleTokenConfirm = () => {
    fetchTokens(selectedIdol);
  };

  const handlePageChange = (page) => {
    fetchTokens(selectedIdol, page);
  };

  return (
    <div>
      <h3 className="mb-4">{t('token_management')}</h3>
      <div className="d-flex justify-content-end align-items-center">
        <div className="d-flex align-items-center gap-4 mb-3">
          <button className="btn btn-primaryF rounded-pill" onClick={handleCreateToken}>
            <FontAwesomeIcon icon={["fas", "plus"]} className="me-2" />
            {t('create_token')}
          </button>
          <Link to="/api-docs" className="hover-p">
            <FontAwesomeIcon icon={["fas", "file-lines"]} className="me-2" />
            API
          </Link>
        </div>
      </div>

      <IdolSelector
        value={selectedIdol}
        onChange={setSelectedIdol}
        className="mb-4"
      />

      {hasData ? (
        <div className="bg-white rounded p-4 mb-4">
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr className="table-light">
                  <th scope="col" className="text-nowrap p-3">{t('token_name')}</th>
                  <th scope="col" className="text-nowrap p-3">{t('creation_time')}</th>
                  <th scope="col" className="text-nowrap p-3">{t('expiry_time')}</th>
                  <th scope="col" className="text-nowrap p-3">{t('status')}</th>
                  <th scope="col" className="text-nowrap p-3">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {tokens.map((token) => (
                  <tr key={token.id}>
                    <td className="p-3 text-nowrap">{token.name}</td>
                    <td className="p-3 text-nowrap">{formatDateTime(token.created_at)}</td>
                    <td className="p-3 text-nowrap">{token.expire_at ? formatDateTime(token.expire_at) : t('permanent')}</td>
                    <td className="p-3 text-nowrap">
                      {token.status === 'active' ? t('active') :
                        token.status === 'expired' ? t('expired') :
                          token.status === 'revoked' ? t('revoked') : token.status}
                    </td>
                    <td className="p-3">
                      {token.status === 'active' && (
                        <button
                          className="btn btn-danger rounded-pill btn-sm text-nowrap"
                          onClick={() => handleClickDelete(token.id)}
                        >
                          {t('revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-5">
          <p>{t('no_data')}</p>
        </div>
      )}

      {/* 分頁 */}
      {total > limit && (
        <Pagination
          total={total}
          limit={limit}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          scrollMode="window"
        />
      )}

      <CreateTokenModal
        ref={createTokenModalRef}
        onSubmit={handleTokenSubmit}
        onConfirm={handleTokenConfirm}
      />

      {/* 撤銷確認 */}
      <MessageModal ref={deleteModalRef} status="warning" size="md" locked={isDeleting}>
        <h5 className="mb-5">{t('token_revoke_confirm')}</h5>
        <div className="text-end">
          <button type="button" className="btn btn-neutral rounded-pill me-3" onClick={() => deleteModalRef.current?.hide()} disabled={isDeleting}>{t('cancel')}</button>
          <LoadingButton className="btn btn-primary rounded-pill" onClick={handleConfirmDelete} isLoading={isDeleting}>{t('confirm')}</LoadingButton>
        </div>
      </MessageModal>

      {/* Success Modal */}
      <MessageModal ref={successModalRef} status="success" size="md">
        <h5 className="mb-5">{t('token_revoked_successfully')}</h5>
      </MessageModal>

      {/* Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </div>
  );
}

export default TokenManagement;